// Uren verdelen — the section where a planner decides which days the work lands on.
//
// ── WHY THIS IS NOT A DATE RANGE ────────────────────────────────────────────────
// A workpackage has a total; the days it sits on are a separate decision and rarely a tidy one. Real
// work is Monday eight and Thursday four, because Tuesday the technician is on another werf. A range
// picker cannot say that, so this is a LIST OF DAYS and each row stands alone.
//
// ── THE THREE NUMBERS ARE DERIVED, NEVER STORED ─────────────────────────────────
// Geschat, gepland, resterend come from `delivery.workpackage_budget`. A stored "remaining" is a
// number that goes wrong the first time somebody edits a day without updating it, and then quietly
// stays wrong.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { correlationId } from '../lib/mutate.ts';
import { addDays, workdaysBetween, type Ymd } from '../lib/dates.ts';
import { fmt } from '../lib/capacity.ts';
import { Banner, Spinner } from './bits.tsx';

/** Hours a planner actually types. The free field covers everything else. */
const PRESETS = [1, 2, 2.5, 4, 6, 8];

const toMin = (h: number) => Math.round(h * 60);

export function HourSplit({ projectId, workpackageId, people }: {
  projectId: number; workpackageId: number; people: A.Person[];
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newDay, setNewDay] = useState<Ymd | ''>('');
  const [newHours, setNewHours] = useState(8);
  const [newResource, setNewResource] = useState<number | ''>('');

  const budget = useQuery({
    queryKey: ['budget', projectId, workpackageId],
    queryFn: () => A.getBudget(projectId, workpackageId),
  });
  const allocs = useQuery({
    queryKey: ['allocations', projectId, workpackageId],
    queryFn: () => A.getAllocations(projectId, workpackageId),
  });

  const naam = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of people) if (p.resourceId !== null) m.set(p.resourceId, p.displayName);
    return m;
  }, [people]);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['budget', projectId, workpackageId] }),
      qc.invalidateQueries({ queryKey: ['allocations', projectId, workpackageId] }),
      qc.invalidateQueries({ queryKey: ['schedule'] }),
    ]);
  };

  const act = async (fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true); setError(null);
    try { await fn(); await refresh(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const list = allocs.data ?? [];
  const b = budget.data;

  /** Which assignment a new day belongs to: the picked person's, or the only one there is. */
  const assignmentFor = (resourceId: number | ''): number | null => {
    if (resourceId !== '') return list.find((a) => a.resourceId === resourceId)?.assignmentId ?? null;
    const only = new Set(list.map((a) => a.assignmentId));
    return only.size === 1 ? [...only][0] : null;
  };

  const addDay = () => act(async () => {
    const aid = assignmentFor(newResource);
    if (!aid) throw new Error('kies eerst een medewerker die al op deze taak staat');
    if (!newDay) throw new Error('kies een datum');
    await A.setAllocation(projectId, {
      assignmentId: aid, day: newDay, minutes: toMin(newHours),
      screen: 'project', source: 'manual', correlationId: correlationId(),
    });
    A.recordEvent({ eventType: 'allocation_created', projectId, workpackageId,
      valueAfter: { day: newDay, minutes: toMin(newHours) }, source: 'manual', screen: 'project' });
    setNewDay('');
  });

  /**
   * Spread what is left over the days that already exist.
   *
   * ONLY OVER EXISTING DAYS, and never inventing new ones. Guessing which days a planner meant is the
   * thing this whole section exists to stop doing; "verdeel wat over is over wat er staat" is a
   * shortcut they can predict.
   */
  const spreadRemaining = () => act(async () => {
    if (!b || b.remainingMinutes <= 0 || list.length === 0) return;
    const per = Math.floor(b.remainingMinutes / list.length);
    let rest = b.remainingMinutes - per * list.length;
    for (const a of list) {
      const extra = per + (rest > 0 ? 1 : 0);
      if (rest > 0) rest--;
      if (extra <= 0) continue;
      await A.setAllocation(projectId, {
        assignmentId: a.assignmentId, day: a.day, minutes: a.minutes + extra,
        before: a.minutes, screen: 'project', source: 'manual', correlationId: correlationId(),
      });
    }
  });

  const setHours = (a: A.DayAllocation, hours: number) => act(async () => {
    await A.setAllocation(projectId, {
      assignmentId: a.assignmentId, day: a.day, minutes: toMin(hours), before: a.minutes,
      screen: 'project', source: 'manual', correlationId: correlationId(),
    });
    A.recordEvent({ eventType: 'allocation_hours_changed', projectId, workpackageId,
      allocationId: a.id, valueBefore: { minutes: a.minutes }, valueAfter: { minutes: toMin(hours), day: a.day },
      source: 'manual', screen: 'project' });
  });

  const remove = (a: A.DayAllocation) => act(async () => {
    await A.setAllocation(projectId, {
      assignmentId: a.assignmentId, day: a.day, minutes: 0, before: a.minutes,
      screen: 'project', source: 'manual', correlationId: correlationId(),
    });
    A.recordEvent({ eventType: 'allocation_deleted', projectId, workpackageId,
      allocationId: a.id, valueBefore: { day: a.day, minutes: a.minutes },
      source: 'manual', screen: 'project' });
  });

  const split = (a: A.DayAllocation) => act(async () => {
    // Half here, half on the next working day. A split with no destination would be a dialog, and the
    // planner can drag the second half wherever they like the moment it exists.
    const keep = Math.max(30, Math.floor(a.minutes / 2));
    let to = addDays(a.day as Ymd, 1);
    while (workdaysBetween(to, to) === 0) to = addDays(to, 1);
    await A.splitAllocation(projectId, {
      assignmentId: a.assignmentId, day: a.day, keepMinutes: keep, toDay: to,
      screen: 'project', correlationId: correlationId(),
    });
  });

  const copy = (a: A.DayAllocation) => act(async () => {
    let to = addDays(a.day as Ymd, 1);
    while (workdaysBetween(to, to) === 0) to = addDays(to, 1);
    await A.setAllocation(projectId, {
      assignmentId: a.assignmentId, day: to, minutes: a.minutes,
      screen: 'project', source: 'copy', correlationId: correlationId(),
    });
    A.recordEvent({ eventType: 'allocation_copied', projectId, workpackageId,
      valueBefore: { day: a.day }, valueAfter: { day: to, minutes: a.minutes },
      source: 'copy', screen: 'project' });
  });

  return (
    <div className="field hoursplit">
      <label>Uren verdelen</label>

      {b && (
        <div className="budget">
          <span><strong>{fmt(b.estimatedMinutes)} u</strong> geschat</span>
          <span><strong>{fmt(b.plannedMinutes)} u</strong> gepland</span>
          <span className={b.remainingMinutes > 0 ? 'left' : ''}>
            <strong>{fmt(b.remainingMinutes)} u</strong> resterend
          </span>
          {/* OVER BUDGET IS A FACT, NOT A REFUSAL. A planner who deliberately books more than the
              estimate is making a decision; the board says so and does not argue. */}
          {b.plannedMinutes > b.estimatedMinutes && (
            <span className="badge over">{fmt(b.plannedMinutes - b.estimatedMinutes)} u boven schatting</span>
          )}
        </div>
      )}

      {error && <Banner kind="err">{error}</Banner>}
      {allocs.isPending && <Spinner />}

      {list.length === 0 && !allocs.isPending && (
        <p className="faint" style={{ margin: 0 }}>
          Nog geen dagen ingepland. De uren staan gelijk verdeeld over het venster tot je hier een dag
          toevoegt of er één versleept in de kalender.
        </p>
      )}

      {list.map((a) => (
        <div key={a.id} className="alloc-row">
          <span className="alloc-day">{a.day}</span>
          <span className="alloc-who">{naam.get(a.resourceId) ?? `#${a.resourceId}`}</span>
          <div className="chips">
            {PRESETS.map((h) => (
              <button key={h} type="button" className={`btn small${Math.abs(a.minutes - toMin(h)) < 1 ? ' primary' : ''}`}
                      disabled={busy} onClick={() => setHours(a, h)}>
                {String(h).replace('.', ',')}u
              </button>
            ))}
          </div>
          <input type="number" min={0} max={24} step={0.25} value={a.minutes / 60} aria-label="uren"
                 disabled={busy}
                 onChange={(e) => { const v = Number(e.target.value); if (v >= 0) setHours(a, v); }} />
          <button type="button" className="btn small" disabled={busy} title="Opsplitsen" onClick={() => split(a)}>⁄</button>
          <button type="button" className="btn small" disabled={busy} title="Kopiëren naar de volgende werkdag" onClick={() => copy(a)}>⧉</button>
          <button type="button" className="btn small danger" disabled={busy} title="Dag verwijderen" onClick={() => remove(a)}>✕</button>
        </div>
      ))}

      <div className="alloc-add">
        <input type="date" value={newDay} aria-label="Datum toevoegen"
               onChange={(e) => setNewDay(e.target.value as Ymd)} />
        <select value={newResource} aria-label="Medewerker"
                onChange={(e) => setNewResource(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">— medewerker —</option>
          {[...new Set(list.map((a) => a.resourceId))].map((r) => (
            <option key={r} value={r}>{naam.get(r) ?? `#${r}`}</option>
          ))}
        </select>
        <input type="number" min={0.25} max={24} step={0.25} value={newHours} aria-label="Uren"
               onChange={(e) => setNewHours(Number(e.target.value))} />
        <button type="button" className="btn small" disabled={busy || !newDay} onClick={addDay}>Dag toevoegen</button>
        <button type="button" className="btn small" disabled={busy || !b || b.remainingMinutes <= 0 || list.length === 0}
                onClick={spreadRemaining} title="Verdeel de resterende uren over de dagen die er al staan">
          Resterend verdelen
        </button>
      </div>
    </div>
  );
}
